import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveShow, userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import CompositeJudgeMapDialog from "./CompositeJudgeMapDialog";
import { ADVISORY_KEYS, otherJudgeIdsOf } from "../lib/compositeJudgeMap";

// Card #1742 — "Composite judges" tab on the Judges page. Lists the show's
// roster and, for each judge, whether it's a plain single row or a composite
// row mapped to a primary judge + its other judges (card #1342's mapping
// entity; #3417: a primary plus 1 to 4 others, 2 to 5 judges in all).
// A mapping may be created/edited/removed at any time, including after the
// composite judge has scored sheets or a bound signature (#1342 AC7 — the
// write path never touches the roster row or the signature pointer), so
// there is deliberately no UI-side block on a signed/scored judge here.
//
// Reads/writes go through the generated score-common client only
// (scoreApi.judges.*CompositeJudgeMap*) — never a direct fetch, never a
// second resolver. `judges` is the SAME list JudgeManager already fetched
// for the roster tab (score-api's roster is the only source of who a
// mapping may target — #1342 AC6, no minting), passed down rather than
// re-fetched here.
//
// Write-action gating: the create/update/delete routes require
// score:manage_judges server-side (already granted to the score_admin role,
// #1342). scoreadmin-fe has no per-component permission check anywhere
// today (grepped) — the ENTIRE admin shell is already gated on the
// score_admin role by ScoreEntitlementGate (App.jsx), which is how
// JudgeManager's existing admin action (signature capture, JudgeDrawer) is
// gated: nobody without score_admin reaches this component at all. This
// panel follows that same posture rather than adding a second, competing
// permission-check pattern that doesn't exist elsewhere in this app.
// #3187 -- `judgesLoading`/`judgesError` are the PARENT's judge-roster fetch
// state. `judges` arrives empty until that fetch settles, so without them the
// panel painted "No judges found" while the roster was still loading (or had
// failed, which the parent already reports in its own alert).
export default function CompositeJudgeMapPanel({ judges, activeShowId, judgesLoading = false, judgesError = null }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  // #2195 — activeShowId comes in as a prop (JudgeManager's own useActiveShow
  // read), but that prop alone can't distinguish "no show yet" from "the
  // show-list fetch just hasn't resolved" (activeShowId is null either way).
  // Reading the same shared context here for its `loading` flag is cheap
  // (ActiveShowContext dedupes across consumers) and keeps this panel correct
  // even if a future caller passes it a still-loading activeShowId directly.
  const { loading: loadingShow } = useActiveShow();

  const [maps, setMaps] = useState([]);
  // #3186 -- `loading` is true while a fetch is in flight AND until the data
  // for the CURRENT show has landed once. A flag only flipped inside the fetch
  // was false for one frame after a show change, so the previous show's list
  // (or the empty text) flashed before the skeleton.
  const [inFlight, setInFlight] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null);
  const loading = inFlight || (Boolean(activeShowId) && loadedFor !== activeShowId);
  const [error, setError] = useState(null);

  const [dialogJudge, setDialogJudge] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);
  // #3417 -- non-blocking advisories (e.g. YOUNG_HORSE_JURY_MAX_3, USEF
  // DR126.2.e.8) the API returned for the mapping that was JUST saved. The
  // save already happened; this only tells the scorer.
  const [savedNotice, setSavedNotice] = useState(null);

  const loadMaps = useCallback(async () => {
    if (!activeShowId) {
      setMaps([]);
      return;
    }
    try {
      setInFlight(true);
      setError(null);
      const list = unwrap(await scoreApi.judges.listCompositeJudgeMaps(activeShowId));
      setMaps(list);
    } catch (err) {
      setError(err);
    } finally {
      setInFlight(false);
      setLoadedFor(activeShowId);
    }
  }, [scoreApi, activeShowId]);

  useEffect(() => {
    loadMaps();
  }, [loadMaps]);

  const judgesById = useMemo(() => {
    const byId = new Map();
    judges.forEach((j) => byId.set(j.id, j));
    return byId;
  }, [judges]);

  const mapsByCompositeId = useMemo(() => {
    const byId = new Map();
    maps.forEach((m) => byId.set(m.compositeJudgeId, m));
    return byId;
  }, [maps]);

  const sortedJudges = useMemo(
    () => [...judges].sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b))),
    [judges],
  );

  const closeMapDialog = () => setDialogJudge(null);

  const handleSaved = (saved) => {
    const advisories = (saved?.advisories || []).filter((a) => ADVISORY_KEYS[a.code]);
    setSavedNotice(
      advisories.length > 0 && dialogJudge
        ? {
            name: userDisplayName(dialogJudge),
            advisories,
            judgeCount: 1 + otherJudgeIdsOf(saved).length,
          }
        : null,
    );
    closeMapDialog();
    loadMaps();
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      setRemoveError(null);
      unwrap(await scoreApi.judges.deleteCompositeJudgeMap(removeTarget.id));
      setRemoveTarget(null);
      loadMaps();
    } catch (err) {
      setRemoveError({ message: err.message || t("judges.compositeTab.removeError") });
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("judges.compositeTab.intro")}
      </Typography>

      {/* #2867 - inline self-service guidance: what a mapping is and how to
          set one, findable without contacting support. Always rendered (not
          only when a row is unmapped). #3631: folded into a collapsed-by-
          default Accordion (never remembered) so it stops costing half a
          screen on every visit; still one labeled click away. */}
      <Accordion
        disableGutters
        variant="outlined"
        elevation={0}
        data-testid="composite-judge-guidance"
        sx={{ mb: 2, "&:before": { display: "none" } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="composite-judge-guidance-content"
          id="composite-judge-guidance-header"
          sx={{ minHeight: 40, "& .MuiAccordionSummary-content": { my: 0.5, alignItems: "center", gap: 1 } }}
        >
          <InfoOutlinedIcon fontSize="small" color="info" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t("judges.compositeTab.guidance.title")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails id="composite-judge-guidance-content">
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("judges.compositeTab.guidance.what")}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t("judges.compositeTab.guidance.stepsTitle")}
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 3, typography: "body2" }}>
            <li>{t("judges.compositeTab.guidance.step1")}</li>
            <li>{t("judges.compositeTab.guidance.step2")}</li>
            <li>{t("judges.compositeTab.guidance.step3")}</li>
            <li>{t("judges.compositeTab.guidance.step4")}</li>
            <li>{t("judges.compositeTab.guidance.step5")}</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t("judges.compositeTab.guidance.ordinaryRow")}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {savedNotice && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="composite-judge-saved-advisory"
          onClose={() => setSavedNotice(null)}
          closeText={t("judges.compositeTab.advisoryDismiss")}
        >
          <AlertTitle>{t("judges.compositeTab.advisoryNoticeTitle", { name: savedNotice.name })}</AlertTitle>
          {savedNotice.advisories.map((a) => (
            <Typography key={a.code} variant="body2">
              {t(ADVISORY_KEYS[a.code], {
                judgeCount: savedNotice.judgeCount,
                maxJudges: a.maxJudges,
              })}
            </Typography>
          ))}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("judges.loadError")}
        </Alert>
      )}

      {!activeShowId ? (
        loadingShow ? (
          <TableSkeleton rows={4} columns={2} />
        ) : (
          <Typography color="text.secondary">{t("judges.noShow")}</Typography>
        )
      ) : loading || judgesLoading ? (
        <TableSkeleton rows={4} columns={2} />
      ) : sortedJudges.length === 0 ? (
        judgesError ? null : <Typography color="text.secondary">{t("judges.empty")}</Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("judges.compositeTab.columns.name")}</TableCell>
                <TableCell>{t("judges.compositeTab.columns.mapping")}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedJudges.map((judge) => {
                const mapping = mapsByCompositeId.get(judge.id) || null;
                const primary = mapping ? judgesById.get(mapping.primaryJudgeId) : null;
                const others = mapping
                  ? otherJudgeIdsOf(mapping).map((id) => {
                      const other = judgesById.get(id);
                      return other ? userDisplayName(other) : "-";
                    })
                  : [];

                return (
                  <TableRow key={judge.id}>
                    <TableCell>{userDisplayName(judge) || "-"}</TableCell>
                    <TableCell>
                      {mapping ? (
                        t("judges.compositeTab.mappedTo", {
                          primary: primary ? userDisplayName(primary) : "-",
                          others: others.join(", "),
                        })
                      ) : (
                        <Typography component="span" color="text.secondary">
                          {t("judges.compositeTab.unmapped")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => setDialogJudge(judge)}
                        sx={{
                          color: colors.foxOrange,
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        {mapping
                          ? t("judges.compositeTab.editButton")
                          : t("judges.compositeTab.mapButton")}
                      </Button>
                      {mapping && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setRemoveError(null);
                            setRemoveTarget(judge);
                          }}
                          sx={{ ml: 1 }}
                        >
                          {t("judges.compositeTab.removeButton")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CompositeJudgeMapDialog
        open={!!dialogJudge}
        onClose={closeMapDialog}
        activeShowId={activeShowId}
        compositeJudge={dialogJudge}
        existingMapping={dialogJudge ? mapsByCompositeId.get(dialogJudge.id) || null : null}
        judgeOptions={dialogJudge ? judges.filter((j) => j.id !== dialogJudge.id) : []}
        onSaved={handleSaved}
      />

      <Dialog
        open={!!removeTarget}
        onClose={() => (removing ? null : setRemoveTarget(null))}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
      >
        <DialogTitle color="text.primary">{t("judges.compositeTab.removeConfirmTitle")}</DialogTitle>
        <DialogContent>
          {removeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {removeError.message}
            </Alert>
          )}
          <Typography color="text.secondary">
            {t("judges.compositeTab.removeConfirmBody", {
              name: removeTarget ? userDisplayName(removeTarget) : "",
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setRemoveTarget(null)}
            disabled={removing}
            sx={{ color: "text.primary" }}
          >
            {t("judges.compositeTab.removeConfirmCancel")}
          </Button>
          <Button color="error" variant="contained" onClick={confirmRemove} disabled={removing}>
            {removing
              ? t("judges.compositeTab.removing")
              : t("judges.compositeTab.removeConfirmButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
